// 保存データの後方互換。読み込んだ Profile（JSON）を今の形にそろえる（その場で書き換えて返す）。
// - 装備の銘1つ（mei）→ 追加効果の配列（affixes）
// - settings（自動売却）が無ければ既定
// - 旧3枝スキルツリー（tree・freeResetUsed）→ キャラ別スキル（skills）。旧割り振りは捨て、全員おすすめ構成から始める
// - お供（companion）が無い旧データは、持っている式神から戦力が最も高い1体を選ぶ（仲間がいなければ null）
// - 式神の作り直し（30体）：小型・中型の式神は消える。★の数を legacyShikiStars に残し、settleLegacyShikigami が銭で補填する。
//   お供が小型・中型だったら外し、エリート以上の仲間がいれば戦力が最も高い1体をお供にする
// - 式神Lv（16.3）：Lv の無い式神は Lv1。★2以上だった分（★-1 の合計）を legacyShikiLvStars に残し、settleLegacyShikiLevels が霊玉で補填する
// - お守り：固定12種（段 stage）→ ランダム生成の個体（主効果だけ・q 中央、課金品は q 高め）。枠は3 → 1（戦力が最も高い1つを残す）。normalizeOmamori
// - 旧スキルツリーのリセット券（tickets.reset）は settleLegacyResetTickets で補填する（資源が要るのでサーバーが呼ぶ）
import { CHAR_IDS, ITEM_IDS, type ItemId } from '../types';
import { ITEM_SLOTS, ITEM_UNLOCK } from './items';
import { CONFIG } from './config';
import { dexMigrateId } from '../data/enemies';
import { AFFIX_POOL, meiToAffix } from './equipment';
import { normalizeOmamori } from './omamori';
import { rateOf, recommendCompanion } from './loadout';
import { levelCap, SHIKIGAMI, stageOf } from './shikigami';

/** 章ボスの式神の付け替え（旧：大将 → 新：本城の番人） */
const SHIKI_MOVED: Record<string, string> = { e020: 'r02', e050: 'r05', e060: 'r06', e070: 'r07', e090: 'r09', e100: 'r10' };
import type { Profile, Resources } from './profile';
import { BUILD_SLOT_KEY, newCharSkills, SKILL_BY_ID } from './skills';
import { isLineToken } from './skill-lines';

export function normalizeProfile(p: Profile): Profile {
  // 図鑑：大将になった妖（敵の操作キャラ）の記録は、その章の番人の記録に置き換える（data/enemies.ts の DEX_LIST）
  if (Array.isArray(p.dex) && p.dex.some((id) => dexMigrateId(id) !== id)) p.dex = [...new Set(p.dex.map(dexMigrateId))];
  for (const item of p.items ?? []) {
    if (!Array.isArray(item.affixes)) item.affixes = item.mei ? [meiToAffix(item.mei, item.tier)] : [];
    if ('mei' in item) delete item.mei;
    // 削除した銘（倹の銘、2026-10-22）は外す
    item.affixes = item.affixes.filter((a) => AFFIX_POOL[a.id]);
  }
  // 装備の強化の目盛りを +100（1段1%）へ：旧の +n（1段5%）は +5n にして強さをそのまま。合成（限界突破）は届いている段まで済ませたことにする
  if (p.equipV !== 2) {
    for (const item of p.items ?? []) {
      item.level = Math.min(100, Math.max(0, Math.round((item.level ?? 0) * 5)));
      item.lb = Math.min(9, Math.max(item.lb ?? 0, Math.ceil(Math.max(0, item.level - 10) / 10)));
    }
    p.equipV = 2;
  }
  if (!p.settings || typeof p.settings !== 'object') p.settings = { autoSell: 'none' };
  if (!['none', 'n', 'r'].includes(p.settings.autoSell)) p.settings.autoSell = 'none';
  // 旧スキルツリー
  const legacy = p as Profile & { tree?: unknown; freeResetUsed?: unknown };
  if ('tree' in legacy) delete legacy.tree;
  if ('freeResetUsed' in legacy) delete legacy.freeResetUsed;
  if (!p.skills || typeof p.skills !== 'object') p.skills = {} as Profile['skills'];
  for (const c of CHAR_IDS) {
    const st = p.skills[c];
    if (!st || typeof st !== 'object' || !Array.isArray(st.set)) {
      p.skills[c] = newCharSkills();
      continue;
    }
    // 系統の Lv の印（@lv:<系統>=<Lv>）も残す
    st.set = st.set.filter((id) => typeof id === 'string' && (SKILL_BY_ID[id] || isLineToken(id)));
    st.auto = st.auto !== false;
    if (!Array.isArray(st.presets)) st.presets = [null, null];
    st.presets = st.presets.map((x) => (Array.isArray(x) ? x.filter((id) => typeof id === 'string' && (SKILL_BY_ID[id] || isLineToken(id))) : null));
  }
  if (!Array.isArray(p.gaidenCleared)) p.gaidenCleared = [];
  if (!Array.isArray(p.battleItems)) p.battleItems = ['heal'];
  // 使役率アップの権利は2倍→3倍に変えた（権利IDも recruit2x → recruit3x）。旧IDは読み替える
  if (Array.isArray(p.entitlements) && p.entitlements.includes('recruit2x')) p.entitlements = [...new Set(p.entitlements.map((e) => (e === 'recruit2x' ? 'recruit3x' : e)))];
  if (!p.shikigami || typeof p.shikigami !== 'object') p.shikigami = {};
  // 章ボスの式神を大将から本城の番人へ移した（2026-10-21）：旧 ID の式神は★・Lv ごと新しい ID へ（お供の指定も）
  for (const [from, to] of Object.entries(SHIKI_MOVED)) {
    const st = p.shikigami[from];
    if (!st) continue;
    const cur = p.shikigami[to];
    if (!cur || (cur.star ?? 0) < (st.star ?? 0)) p.shikigami[to] = st;
    delete p.shikigami[from];
    if (p.companion === from) p.companion = to;
    for (const c of CHAR_IDS) if (p.kits?.[c]?.companion === from) p.kits[c]!.companion = to;
  }
  // 式神にならない妖（小型・中型・不明）の記録を消し、★の数を補填用に残す
  let lost = 0;
  let lostCompanion = false;
  let lvStars = 0;
  let refund = 0;
  for (const [id, st] of Object.entries(p.shikigami)) {
    if (SHIKIGAMI[id]) {
      // 天井は廃止：旧データの天井カウンタ（pity）は捨てる
      if (st && typeof st === 'object' && 'pity' in st) delete (st as { pity?: number }).pity;
      if (st && typeof st === 'object') {
        // 式神Lv：無い旧データは Lv1 から（★はそのまま限界突破の段階）。★を上げていた分は霊玉で補填する
        if (typeof st.level !== 'number' || !Number.isFinite(st.level)) {
          lvStars += Math.max(0, Math.floor(Number(st.star) || 0) - 1);
          st.level = 1;
        }
        st.level = st.star > 0 ? Math.max(1, Math.min(levelCap(st.star), Math.floor(st.level))) : 1;
        // ★＝進化の段階（式神Lv で決まる。1〜3）
        if (st.star > 0) st.star = Math.max(1, Math.min(3, stageOf(st.level)));
      }
      continue;
    }
    lost += Math.max(0, Math.floor(Number(st?.star) || 0));
    // 式神の作り直し（10種族）で消えた式神：上げていた式神Lv の霊玉を返す（旧の費用の式：Lv1→2 が5、Lv49→50 が60）
    const oldLv = Math.max(1, Math.min(50, Math.floor(Number(st?.level) || 1)));
    for (let L = 1; L < oldLv; L++) refund += Math.round(5 + 55 * Math.pow(Math.min(1, (L - 1) / 48), 1.2));
    if (p.companion === id) lostCompanion = true;
    delete p.shikigami[id];
  }
  if (lost > 0) p.legacyShikiStars = (p.legacyShikiStars ?? 0) + lost;
  if (lvStars > 0) p.legacyShikiLvStars = (p.legacyShikiLvStars ?? 0) + lvStars;
  if (refund > 0) p.legacyShikiReidama = (p.legacyShikiReidama ?? 0) + refund;
  if (p.companion === undefined || lostCompanion) p.companion = recommendCompanion(p, p.oshi).enemyId;
  else if (typeof p.companion !== 'string' || !SHIKIGAMI[p.companion] || (p.shikigami[p.companion]?.star ?? 0) <= 0) p.companion = null;
  // 式神・道具はキャラごと（kits）。無いキャラには旧の共通設定を写す。持っていない式神・知らない道具は外す
  const owned = (id: unknown) => typeof id === 'string' && !!SHIKIGAMI[id] && (p.shikigami[id]?.star ?? 0) > 0;
  const kits: NonNullable<Profile['kits']> = {};
  for (const c of CHAR_IDS) {
    const k = p.kits && typeof p.kits === 'object' ? p.kits[c] : undefined;
    const companion = k && 'companion' in k ? (owned(k.companion) ? k.companion : null) : p.companion;
    const raw = k && Array.isArray(k.items) ? k.items : p.battleItems;
    const items = raw.filter((id, i, a): id is ItemId => typeof id === 'string' && ITEM_IDS.includes(id as ItemId) && a.indexOf(id) === i).slice(0, ITEM_SLOTS);
    kits[c] = { companion, items: items.length ? items : ['heal'] };
  }
  p.kits = kits;
  // 道具は万屋で解放する形に（2026-10-21）。それより前のデータは、主Lv で解放済みだった道具と、持ち出しに選んでいた道具を持っていることにする
  if (!Array.isArray(p.ownedItems)) {
    const had = new Set<ItemId>(ITEM_IDS.filter((id) => id !== 'heal' && (p.level ?? 1) >= ITEM_UNLOCK[id]));
    for (const c of CHAR_IDS) for (const id of kits[c]?.items ?? []) if (id !== 'heal') had.add(id);
    p.ownedItems = ITEM_IDS.filter((id) => had.has(id));
  } else p.ownedItems = p.ownedItems.filter((id, i, a): id is ItemId => typeof id === 'string' && id !== 'heal' && ITEM_IDS.includes(id as ItemId) && a.indexOf(id) === i);
  // 万屋：旧の日替わり（買った品の番号）→ 品物ごとの今日の購入数
  if (!p.shop || typeof p.shop !== 'object' || typeof p.shop.count !== 'object' || !p.shop.count) p.shop = { day: '', count: {} };
  if ('bought' in p.shop) delete (p.shop as { bought?: unknown }).bought;
  // 旧の共通の欄は出撃キャラの持ち物の写し
  p.companion = kits[p.oshi]?.companion ?? null;
  p.battleItems = [...(kits[p.oshi]?.items ?? ['heal'])];
  if (!p.tickets || typeof p.tickets !== 'object') p.tickets = { rename: 0 };
  if (typeof p.tickets.rename !== 'number') p.tickets.rename = 0;
  // お守りのランダム生成（GAME_DESIGN 18章）：固定12種（段・課金品）→ 主効果だけ・効果の大きさ中央の個体。おまもりガチャ・御朱印・交換所の既定。
  // 枠は1つ（旧3枠に付いていた分は、そのキャラの戦力が最も高くなる1つを残す）
  normalizeOmamori(p, rateOf);
  return p;
}

/**
 * 旧スキルツリーのリセット券（課金・補填で持っていた分）の補填。霊玉は付けない。
 * 1枚目は「スキル構成の保存枠」（build_slots）、まだ持っていれば／2枚目以降は1枚ごとに銭 legacyResetZeni。
 * extra は受け取り箱にあった旧券（reset_ticket）の枚数。処理した券は消える（二重には補填しない）。
 */
export function settleLegacyResetTickets(p: Profile, res: Resources, extra = 0): { tickets: number; zeni: number; buildSlots: boolean } {
  const n = Math.max(0, Math.floor(p.tickets?.reset ?? 0)) + Math.max(0, Math.floor(extra));
  if (p.tickets && 'reset' in p.tickets) delete p.tickets.reset;
  if (n <= 0) return { tickets: 0, zeni: 0, buildSlots: false };
  let rest = n;
  let buildSlots = false;
  if (!p.entitlements.includes(BUILD_SLOT_KEY)) {
    p.entitlements.push(BUILD_SLOT_KEY);
    buildSlots = true;
    rest--;
  }
  const zeni = rest * CONFIG.skills.legacyResetZeni;
  res.zeni += zeni;
  return { tickets: n, zeni, buildSlots };
}

/**
 * 式神の作り直し（30体）で消えた小型・中型の式神の補填：★1つごとに銭 legacyStarZeni（霊玉は付けない）。
 * normalizeProfile が数えた legacyShikiStars を払って消す（二重には払わない。資源が要るのでサーバーが呼ぶ）
 */
export function settleLegacyShikigami(p: Profile, res: Resources): { stars: number; zeni: number } {
  const stars = Math.max(0, Math.floor(p.legacyShikiStars ?? 0));
  if ('legacyShikiStars' in p) delete p.legacyShikiStars;
  if (stars <= 0) return { stars: 0, zeni: 0 };
  const zeni = stars * CONFIG.shikigami.legacyStarZeni;
  res.zeni += zeni;
  return { stars, zeni };
}

/**
 * 式神Lv の導入（GAME_DESIGN 16.3）の補填：旧仕様で★を上げていた分（★-1 つごと）に霊玉 legacyStarReidama。
 * normalizeProfile が数えた legacyShikiLvStars を払って消す（二重には払わない。資源が要るのでサーバーが呼ぶ）。課金の経路ではない
 */
export function settleLegacyShikiLevels(p: Profile, res: Resources): { stars: number; reidama: number } {
  const stars = Math.max(0, Math.floor(p.legacyShikiLvStars ?? 0));
  const back = Math.max(0, Math.floor(p.legacyShikiReidama ?? 0));
  if ('legacyShikiLvStars' in p) delete p.legacyShikiLvStars;
  if ('legacyShikiReidama' in p) delete p.legacyShikiReidama;
  if (stars <= 0 && back <= 0) return { stars: 0, reidama: 0 };
  const reidama = stars * CONFIG.shikigami.legacyStarReidama + back;
  res.reidama += reidama;
  return { stars, reidama };
}
